import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import initialIcon from 'assets/images/empty-providers.svg';
import themeData from 'utils/theme/themeData';
import { status } from 'constants/general';
import categoriesStrings from 'modules/categories/strings';
import AddEntityDiscountModal from 'modules/modals/AddEntityDiscountModal';
import DoctorInfoModal from 'modules/modals/DoctorInfoModal';
import SearchBar from 'modules/common/components/SearchBar/SearchBar';
import Table from 'modules/common/components/Table';
import { TableItemType } from 'modules/common/components/Table/enums';

import {
  Container,
  Center,
  Img,
  Text,
  Button,
  Wrapper,
  OrderNumberOverlay,
} from './ProviderDiscountsComponent.style';

const ProviderDiscountsComponent = ({
  entityProviderInfo,
  isCreateProviderModalHidden,
  setIsCreateProviderModalHidden,
  searchQuery,
  handleSearchBarInputChange,
  handleShowMoreClick,
  handleAddProvider,
  handleEditProvider,
  handleRemoveProvider,
}) => {
  const entityProviders = useSelector(state => state.categories.entityProviders);

  const [entityProvidersRecords, setEntityProvidersRecords] = useState([]);
  const [showCopiedTextOverlay, setShowCopiedTextOverlay] = useState(false);

  const handleTextToCopyToClipboardClick = textToCopy => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopiedTextOverlay(true);

    setTimeout(() => {
      setShowCopiedTextOverlay(false);
    }, 2000);
  };

  const getMainRowItems = () => {
    const rows = [];

    entityProviders.data?.forEach(item => {
      const { id, providerKey, providerName, providerPhoto } = item;

      rows.push({
        id,
        rowData: item,
        rowContent: [
          {
            itemContent: providerName,
            itemContentURL: providerPhoto,
            itemType: TableItemType.TextWithPhoto,
            itemPath: `${item.id}`,
            isItemClickable: false,
          },
          {
            itemContent: providerKey,
            itemType: TableItemType.Text,
            isItemClickable: true,
            itemClickAction: () => {
              handleTextToCopyToClipboardClick(providerKey);
            },
          },
        ],
        editButtons: [
          {
            btnId: 1,
            btnIcon: '"\\e90a"',
            btnLabel: 'Edit',
            btnFn: () => handleEditProvider(item),
            btnTextColor: themeData.colors.black,
          },
          {
            btnId: 2,
            btnIcon: '"\\e91d"',
            btnLabel: 'Remove',
            btnFn: () => handleRemoveProvider(item),
            btnTextColor: themeData.colors.redMain,
          },
        ],
      });
    });

    return rows;
  };

  useEffect(() => {
    setEntityProvidersRecords(() => getMainRowItems());
  }, [entityProviders?.data]);

  return (
    <Container>
      {entityProviders.status === status.SUCCESS &&
      entityProviders.data.length === 0 &&
      !searchQuery ? (
        <Center>
          <Img src={initialIcon} />
          <Text>{categoriesStrings.providerDiscounts.emptyState}</Text>
          <Button onClick={handleAddProvider}>
            {categoriesStrings.providerDiscounts.addProvider}
          </Button>
        </Center>
      ) : (
        <Wrapper>
          <Button style={{ marginLeft: 'auto' }} onClick={handleAddProvider}>
            {categoriesStrings.providerDiscounts.addProvider}
          </Button>
          <SearchBar
            searchQuery={searchQuery}
            handleSearchBarInputChange={handleSearchBarInputChange}
            testId="healthplansearch"
          />
          <Table
            latestDataLength={entityProviders.latestDataLength}
            dataStatus={entityProviders.status}
            columns={categoriesStrings.providerDiscounts.gridHeaderList}
            rows={entityProvidersRecords}
            searchQuery={searchQuery}
            handleShowMoreClick={handleShowMoreClick}
          />
        </Wrapper>
      )}

      {!isCreateProviderModalHidden && (
        <AddEntityDiscountModal
          entityProviderInfo={entityProviderInfo}
          onClose={() => setIsCreateProviderModalHidden(true)}
        />
      )}
      {showCopiedTextOverlay && <OrderNumberOverlay>Copied to clipboard 🚀</OrderNumberOverlay>}
    </Container>
  );
};

ProviderDiscountsComponent.propTypes = {
  isCreateProviderModalHidden: PropTypes.bool.isRequired,
  setIsCreateProviderModalHidden: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  handleSearchBarInputChange: PropTypes.func.isRequired,
  providersQueryString: PropTypes.shape({
    healthPlanId: PropTypes.number,
    categoryId: PropTypes.number,
    pageNumber: PropTypes.number,
    pagesize: PropTypes.number,
    active: PropTypes.bool,
  }).isRequired,
  handleRemoveProvider: PropTypes.func.isRequired,
};

export default ProviderDiscountsComponent;
